import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, ClipboardPaste, Copy, ArrowLeft, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { payrollAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const BOOKMARKLET = "javascript:(function(){if(window.__skzCap){alert('Capture Sokuzai déjà active — changez de semaine dans Combo puis cliquez le bouton vert.');return;}window.__skzCap=[];window.__skzTot=0;function keep(u,t){try{if(!t||t.length<50)return;var s=t.trim();if(s[0]!=='{'&&s[0]!=='[')return;window.__skzCap.push({url:String(u),body:t});}catch(e){}}var of=window.fetch;window.fetch=function(){var a=arguments;window.__skzTot++;return of.apply(this,a).then(function(r){try{r.clone().text().then(function(t){keep((a[0]&&a[0].url)||a[0],t);});}catch(e){}return r;});};var oo=XMLHttpRequest.prototype.open;XMLHttpRequest.prototype.open=function(m,u){this.addEventListener('load',function(){window.__skzTot++;try{keep(u,this.responseText);}catch(e){}});return oo.apply(this,arguments);};var b=document.createElement('button');b.style.cssText='position:fixed;bottom:16px;right:16px;z-index:2147483647;background:#059669;color:#fff;border:0;border-radius:9999px;padding:12px 18px;font-weight:700;font-size:14px;cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,.35)';b.textContent='Copier pour Sokuzai (0/0)';setInterval(function(){b.textContent='Copier pour Sokuzai ('+window.__skzCap.length+'/'+window.__skzTot+')';},800);b.onclick=function(){if(!window.__skzCap.length){alert('Rien capturé ('+window.__skzTot+' requêtes vues). Changez de semaine ou de vue dans Combo pour forcer le rechargement des données, puis réessayez.');return;}var d=JSON.stringify(window.__skzCap);navigator.clipboard.writeText(d).then(function(){alert('Copié ('+window.__skzCap.length+' réponses) ! Collez dans Sokuzai → Masse salariale → Import Combo.');},function(){prompt('Copie auto impossible — copiez manuellement :',d);});};document.body.appendChild(b);alert('Capture activée ! IMPORTANT : changez maintenant de semaine (flèches) ou de vue dans Combo pour forcer le rechargement des données — le compteur du bouton vert doit augmenter.');})();";

const IGNORE = '__ignore__';

export const ComboImportDialog = ({ open, onClose, onImported }) => {
    const [step, setStep] = useState('input');
    const [rawText, setRawText] = useState('');
    const [analyzing, setAnalyzing] = useState(false);
    const [result, setResult] = useState(null);
    const [mapping, setMapping] = useState({});
    const [confirming, setConfirming] = useState(false);

    const reset = () => { setStep('input'); setRawText(''); setResult(null); setMapping({}); };
    const handleClose = () => { reset(); onClose(); };

    const copyBookmarklet = () => {
        navigator.clipboard.writeText(BOOKMARKLET)
            .then(() => toast.success('Bookmarklet copié — créez un favori et collez-le comme URL'))
            .catch(() => toast.error('Copie impossible'));
    };

    const handleAnalyze = async () => {
        if (!rawText.trim()) { toast.error('Collez d\'abord les données Combo'); return; }
        setAnalyzing(true);
        try {
            const r = await payrollAPI.comboAnalyze(rawText);
            setResult(r.data);
            setMapping(r.data.mapping || {});
            if (!r.data.rows?.length) {
                toast.warning(r.data.note || 'Aucune donnée de masse salariale détectée');
            } else {
                setStep('preview');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de l\'analyse');
        } finally {
            setAnalyzing(false);
        }
    };

    // Lignes finales : équipes mappées → {site_id, date, amount} (cumul si plusieurs équipes sur un même site/jour)
    const entries = useMemo(() => {
        if (!result) return [];
        const agg = {};
        for (const r of result.rows) {
            const sid = mapping[r.team];
            if (!sid || sid === IGNORE) continue;
            const k = `${sid}|${r.date}`;
            agg[k] = (agg[k] || 0) + r.amount;
        }
        return Object.entries(agg).map(([k, amount]) => {
            const [site_id, date] = k.split('|');
            return { site_id, date, amount: Math.round(amount * 100) / 100 };
        });
    }, [result, mapping]);

    const conflicts = useMemo(() => entries.filter(e => {
        const ex = result?.existing?.[e.site_id]?.[e.date];
        return ex != null && Math.abs(ex - e.amount) > 0.01;
    }).length, [entries, result]);

    const siteName = (id) => result?.sites?.find(s => s.id === id)?.name || id;

    const handleConfirm = async () => {
        if (!entries.length) { toast.error('Aucune ligne mappée à importer'); return; }
        setConfirming(true);
        try {
            const cleanMapping = Object.fromEntries(
                Object.entries(mapping).filter(([, v]) => v && v !== IGNORE)
            );
            const r = await payrollAPI.comboConfirm(entries, cleanMapping);
            toast.success(`${r.data.saved} case(s) de masse salariale importée(s)`);
            handleClose();
            onImported?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de l\'import');
        } finally {
            setConfirming(false);
        }
    };

    const fmtDay = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="combo-import-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-emerald-500" />
                        Import Combo — Masse salariale
                    </DialogTitle>
                    <DialogDescription>
                        {step === 'input'
                            ? 'Capturez les données depuis Combo puis collez-les ici pour analyse IA.'
                            : 'Vérifiez les montants, associez les équipes Combo aux sites FE, puis confirmez.'}
                    </DialogDescription>
                </DialogHeader>

                {step === 'input' && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2 text-xs text-slate-600">
                            <p className="font-semibold text-slate-700">1. Installer le bookmarklet (une seule fois)</p>
                            <p>Copiez le code ci-dessous, créez un favori dans votre navigateur nommé « Capturer pour Sokuzai » et collez le code dans le champ URL du favori.</p>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={copyBookmarklet} data-testid="combo-copy-bookmarklet-btn">
                                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copier le bookmarklet
                            </Button>
                            <p className="font-semibold text-slate-700 pt-1">2. Capturer dans Combo</p>
                            <p>Ouvrez Combo → cliquez le favori → <strong>changez de semaine (flèches) ou de vue</strong> pour forcer le rechargement des données (le compteur du bouton vert doit augmenter) → cliquez le bouton vert « Copier pour Sokuzai ».</p>
                            <p className="font-semibold text-slate-700 pt-1">3. Coller ici</p>
                            <p>Collez ci-dessous (fonctionne aussi avec un JSON copié depuis F12 → Réseau → Réponse).</p>
                        </div>
                        <Textarea
                            value={rawText}
                            onChange={(e) => setRawText(e.target.value)}
                            placeholder='Collez ici les données capturées (JSON)…'
                            className="min-h-[140px] font-mono text-xs"
                            data-testid="combo-raw-textarea"
                        />
                        {result && !result.rows?.length && result.note && (
                            <p className="text-xs text-amber-600 flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5" /> {result.note}
                            </p>
                        )}
                    </div>
                )}

                {step === 'preview' && result && (
                    <div className="space-y-4">
                        {/* Mapping équipes → sites */}
                        <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                            <p className="text-xs font-semibold text-slate-700">Équipes Combo → Sites FE</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {result.teams.map(team => (
                                    <div key={team} className="flex items-center gap-2">
                                        <span className="text-xs text-slate-600 flex-1 truncate" title={team}>{team}</span>
                                        <Select
                                            value={mapping[team] || IGNORE}
                                            onValueChange={(v) => setMapping(prev => ({ ...prev, [team]: v }))}
                                        >
                                            <SelectTrigger className="h-8 text-xs w-40" data-testid={`combo-mapping-${team}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={IGNORE}>— Ignorer —</SelectItem>
                                                {result.sites.map(s => (
                                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {result.note && <p className="text-xs text-slate-500 italic">{result.note}</p>}
                        {conflicts > 0 && (
                            <p className="text-xs text-amber-600 flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                {conflicts} case(s) déjà saisie(s) avec un montant différent seront écrasées.
                            </p>
                        )}

                        {/* Aperçu des lignes à importer */}
                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-xs">
                                <thead className="bg-slate-50">
                                    <tr className="text-slate-500">
                                        <th className="text-left px-3 py-1.5">Site</th>
                                        <th className="text-left px-3 py-1.5">Date</th>
                                        <th className="text-right px-3 py-1.5">Montant</th>
                                        <th className="text-right px-3 py-1.5">Déjà saisi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.sort((a, b) => a.date.localeCompare(b.date) || siteName(a.site_id).localeCompare(siteName(b.site_id))).map(e => {
                                        const ex = result.existing?.[e.site_id]?.[e.date];
                                        const conflict = ex != null && Math.abs(ex - e.amount) > 0.01;
                                        return (
                                            <tr key={`${e.site_id}|${e.date}`} className={`border-t ${conflict ? 'bg-amber-50' : ''}`}>
                                                <td className="px-3 py-1 font-medium">{siteName(e.site_id).replace('FE ', '')}</td>
                                                <td className="px-3 py-1 tabular-nums">{fmtDay(e.date)}</td>
                                                <td className="px-3 py-1 text-right font-semibold text-emerald-700 tabular-nums">{formatCurrency(e.amount)}</td>
                                                <td className={`px-3 py-1 text-right tabular-nums ${conflict ? 'text-amber-600 font-semibold' : 'text-slate-400'}`}>
                                                    {ex != null ? formatCurrency(ex) : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {entries.length === 0 && (
                                        <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">Associez au moins une équipe à un site pour voir l'aperçu</td></tr>
                                    )}
                                </tbody>
                                {entries.length > 0 && (
                                    <tfoot className="bg-slate-50 border-t">
                                        <tr>
                                            <td colSpan={2} className="px-3 py-1.5 font-semibold text-right">Total ({entries.length} cases) :</td>
                                            <td className="px-3 py-1.5 text-right font-bold text-emerald-700 tabular-nums">
                                                {formatCurrency(entries.reduce((s, e) => s + e.amount, 0))}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    {step === 'input' ? (
                        <>
                            <Button variant="outline" size="sm" onClick={handleClose}>Annuler</Button>
                            <Button size="sm" onClick={handleAnalyze} disabled={analyzing || !rawText.trim()}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="combo-analyze-btn">
                                <ClipboardPaste className="w-4 h-4 mr-1.5" />
                                {analyzing ? 'Analyse IA en cours…' : 'Analyser'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" size="sm" onClick={() => setStep('input')} data-testid="combo-back-btn">
                                <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour
                            </Button>
                            <Button size="sm" onClick={handleConfirm} disabled={confirming || !entries.length}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="combo-confirm-btn">
                                <Check className="w-4 h-4 mr-1.5" />
                                {confirming ? 'Import…' : `Confirmer l'import (${entries.length})`}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ComboImportDialog;
